import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { tournamentsService } from '../../services/supabaseService';
import { supabase } from '../../lib/supabase';
import Header from '../../components/navigation/Header';
import MetricCard from './components/MetricCard';
import QuickActionCard from './components/QuickActionCard';
import ActivityItem from './components/ActivityItem';

import SystemAlert from './components/SystemAlert';
import RecentTournamentCard from './components/RecentTournamentCard';
import Button from '../../components/ui/Button';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [metrics, setMetrics] = useState({
    activeTournaments: 0,
    totalParticipants: 0,
    pendingResults: 0,
    systemAlerts: 0
  });
  const [recentTournaments, setRecentTournaments] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  useEffect(() => {
    fetchAdminData();
  }, [navigate]);

  const fetchAdminData = async () => {
    try {
      setLoading(true);

      // Fetch tournaments
      const tournaments = await tournamentsService?.getAll();
      
      // Calculate metrics
      const activeTournaments = tournaments?.filter(t => t?.status === 'active')?.length || 0;
      
      // Get date ranges for current and last month
      const now = new Date();
      const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0, 23, 59, 59);

      // Get total participants count (all time)
      const { count: participantsCount } = await supabase?.from('tournament_participants')?.select('*', { count: 'exact', head: true });

      // Get current month participants
      const { count: currentMonthParticipants } = await supabase
        ?.from('tournament_participants')
        ?.select('*', { count: 'exact', head: true })
        ?.gte('joined_at', currentMonthStart?.toISOString());

      // Get last month participants
      const { count: lastMonthParticipants } = await supabase
        ?.from('tournament_participants')
        ?.select('*', { count: 'exact', head: true })
        ?.gte('joined_at', lastMonthStart?.toISOString())
        ?.lte('joined_at', lastMonthEnd?.toISOString());

      // Calculate percentage change for participants
      let participantsTrend = 'neutral';
      let participantsTrendValue = '0%';
      if (lastMonthParticipants > 0) {
        const percentChange = ((currentMonthParticipants - lastMonthParticipants) / lastMonthParticipants) * 100;
        participantsTrend = percentChange > 0 ? 'up' : percentChange < 0 ? 'down' : 'neutral';
        participantsTrendValue = `${percentChange > 0 ? '+' : ''}${Math.round(percentChange)}%`;
      } else if (currentMonthParticipants > 0) {
        participantsTrend = 'up';
        participantsTrendValue = '+100%';
      }

      // Get pending results count (matches with status 'completed' but no winner)
      const { count: pendingResultsCount } = await supabase?.from('matches')?.select('*', { count: 'exact', head: true })?.eq('status', 'completed')?.is('winner_id', null);

      // Get current month notifications count (as proxy for system alerts)
      const { count: currentMonthAlerts } = await supabase
        ?.from('notifications')
        ?.select('*', { count: 'exact', head: true })
        ?.gte('created_at', currentMonthStart?.toISOString());

      // Get last month notifications count
      const { count: lastMonthAlerts } = await supabase
        ?.from('notifications')
        ?.select('*', { count: 'exact', head: true })
        ?.gte('created_at', lastMonthStart?.toISOString())
        ?.lte('created_at', lastMonthEnd?.toISOString());

      // Calculate change in alerts (absolute difference, not percentage)
      let alertsTrend = 'neutral';
      let alertsTrendValue = '0';
      const alertsChange = currentMonthAlerts - lastMonthAlerts;
      if (alertsChange !== 0) {
        alertsTrend = alertsChange > 0 ? 'up' : 'down';
        alertsTrendValue = `${alertsChange > 0 ? '+' : ''}${alertsChange}`;
      }

      setMetrics({
        activeTournaments,
        totalParticipants: participantsCount || 0,
        pendingResults: pendingResultsCount || 0,
        systemAlerts: currentMonthAlerts || 0,
        participantsTrend,
        participantsTrendValue,
        alertsTrend,
        alertsTrendValue
      });

      // Transform recent tournaments
      const recentThree = tournaments?.slice(0, 3) || [];

      // Fetch predictions counts for recent tournaments
      const predictionCounts = await Promise.all(
        recentThree?.map(async (tournament) => {
          const { count: tournamentPredCount } = await supabase
            ?.from('tournament_predictions')
            ?.select('*', { count: 'exact', head: true })
            ?.eq('tournament_id', tournament?.id);

          const { count: matchPredCount } = await supabase
            ?.from('match_predictions')
            ?.select('match_id, matches!inner(tournament_id)', { count: 'exact', head: true })
            ?.eq('matches.tournament_id', tournament?.id);

          return { id: tournament?.id, count: (tournamentPredCount || 0) + (matchPredCount || 0) };
        })
      );
      const predictionCountMap = Object.fromEntries(predictionCounts?.map(p => [p?.id, p?.count]));

      const transformedTournaments = recentThree?.map(tournament => ({
        id: tournament?.id,
        name: tournament?.name,
        format: tournament?.format,
        status: tournament?.status,
        startDate: tournament?.startDate,
        endDate: tournament?.endDate,
        participants: tournament?.tournamentParticipants?.length || 0,
        predictions: predictionCountMap?.[tournament?.id] || 0,
        image: tournament?.bannerImage || 'https://images.unsplash.com/photo-1709307060138-97cc2ee11b99',
        imageAlt: tournament?.bannerImageAlt || 'Tournament banner'
      }));

      setRecentTournaments(transformedTournaments);

      // Fetch system alerts dynamically
      await fetchSystemAlerts(tournaments, pendingResultsCount);

      // Fetch recent activities from activity_feed table
      await fetchRecentActivities();
    } catch (err) {
      console.error('Error fetching admin data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchSystemAlerts = async (tournaments, pendingResultsCount) => {
    const systemAlerts = [];

    // Check for prediction deadlines approaching (within 2 days)
    const now = new Date();
    const twoDaysFromNow = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
    
    const approachingDeadlines = tournaments?.filter(t => {
      if (!t?.predictionLockDate) return false;
      const lockDate = new Date(t?.predictionLockDate);
      return lockDate > now && lockDate <= twoDaysFromNow;
    });

    if (approachingDeadlines?.length > 0) {
      const tournament = approachingDeadlines?.[0];
      const lockDate = new Date(tournament?.predictionLockDate);
      const daysRemaining = Math.ceil((lockDate - now) / (1000 * 60 * 60 * 24));
      
      // Count users who haven't submitted predictions for this tournament
      const { count: totalParticipants } = await supabase
        ?.from('tournament_participants')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tournament_id', tournament?.id);

      const { count: submittedPredictions } = await supabase
        ?.from('tournament_predictions')
        ?.select('*', { count: 'exact', head: true })
        ?.eq('tournament_id', tournament?.id);

      const usersNotSubmitted = (totalParticipants || 0) - (submittedPredictions || 0);

      systemAlerts?.push({
        type: "warning",
        title: "Prediction Deadline Approaching",
        message: `${tournament?.name} tournament-level predictions will auto-lock in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}. ${usersNotSubmitted} users haven't submitted yet.`,
        actionLabel: "Send Reminder",
        onAction: () => console.log("Sending reminder emails")
      });
    }

    // Check for pending results
    if (pendingResultsCount > 0) {
      systemAlerts?.push({
        type: "error",
        title: "Result Posting Pending",
        message: `${pendingResultsCount} match result${pendingResultsCount !== 1 ? 's are' : ' is'} pending. Automated points calculation is waiting for result submission.`,
        actionLabel: "Post Results",
        onAction: () => navigate('/results-management')
      });
    }

    setAlerts(systemAlerts);
  };

  const fetchRecentActivities = async () => {
    try {
      const { data, error } = await supabase
        ?.from('activity_feed')
        ?.select(`
          *,
          user_profiles!inner(full_name)
        `)
        ?.order('timestamp', { ascending: false })
        ?.limit(8);

      if (error) {
        console.error('Error fetching activities:', error);
        return;
      }

      const transformedActivities = data?.map(activity => ({
        type: activity?.activity_type || 'system',
        user: activity?.user_profiles?.full_name || 'System',
        action: activity?.title || '',
        timestamp: new Date(activity?.timestamp),
        details: activity?.description || ''
      })) || [];

      setRecentActivities(transformedActivities);
    } catch (err) {
      console.error('Error fetching recent activities:', err);
    }
  };

  const handleCheckDeadlineReminders = async () => {
    setReminderLoading(true);
    setReminderResult(null);
    try {
      const { data, error } = await supabase?.functions?.invoke('check-deadline-reminders');
      if (error) {
        setReminderResult({ success: false, message: error?.message || 'Failed to invoke function' });
      } else {
        setReminderResult({ success: true, message: data?.message || 'Deadline reminders checked successfully' });
      }
    } catch (err) {
      setReminderResult({ success: false, message: err?.message || 'Unexpected error occurred' });
    } finally {
      setReminderLoading(false);
    }
  };

  const metricsData = [
    {
      title: "Active Tournaments",
      value: metrics?.activeTournaments?.toString(),
      icon: "Trophy",
      trend: "neutral",
      trendValue: "0",
      color: "primary"
    },
    {
      title: "Total Participants",
      value: metrics?.totalParticipants?.toString(),
      icon: "Users",
      trend: metrics?.participantsTrend || "neutral",
      trendValue: metrics?.participantsTrendValue || "0%",
      color: "success"
    },
    {
      title: "Pending Results",
      value: metrics?.pendingResults?.toString(),
      icon: "Clock",
      trend: "neutral",
      trendValue: "0%",
      color: "warning"
    },
    {
      title: "System Alerts",
      value: metrics?.systemAlerts?.toString(),
      icon: "AlertCircle",
      trend: metrics?.alertsTrend || "neutral",
      trendValue: metrics?.alertsTrendValue || "0",
      color: "error"
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  const quickActions = [
  {
    title: "Create Tournament",
    description: "Set up a new PowerPlay tournament with custom rules and prize configurations",
    icon: "PlusCircle",
    actionLabel: "Create New",
    actionPath: "/tournament-management/new",
    color: "primary"
  },
  {
    title: "Manage Users",
    description: "View and manage user accounts, permissions, and participation history",
    icon: "Users",
    actionLabel: "View Users",
    actionPath: "/user-management",
    color: "success"
  },
  {
    title: "Post Results",
    description: "Update match results and trigger automated points calculation for leaderboards",
    icon: "CheckCircle",
    actionLabel: "Post Results",
    actionPath: "/results-management",
    color: "warning"
  },
  {
    title: "View Reports",
    description: "Access detailed analytics and reports on tournament performance and user engagement",
    icon: "BarChart3",
    actionLabel: "View Reports",
    actionPath: "/reports",
    color: "primary"
  }];


  const adminTools = [
    {
      icon: "Trophy",
      title: "Tournament Management",
      description: "Create and configure tournaments, manage teams and schedules",
      color: "bg-blue-500",
      action: () => navigate('/tournament-management')
    },
    {
      icon: "Shield",
      title: "Global Team Management",
      description: "Manage cricket teams, players, and base squad configurations",
      color: "bg-green-500",
      action: () => navigate('/global-team-management')
    },
    {
      icon: "Target",
      title: "Results Management",
      description: "Post match results and calculate prediction scores",
      color: "bg-purple-500",
      action: () => navigate('/results-management')
    },
    {
      icon: "Award",
      title: "Winner Announcements",
      description: "Announce tournament winners and distribute prizes",
      color: "bg-yellow-500",
      action: () => console.log("Navigate to winner announcements")
    },
    {
      icon: "Settings",
      title: "System Settings",
      description: "Configure platform settings and user permissions",
      color: "bg-gray-500",
      action: () => console.log("Navigate to system settings")
    },
    {
      icon: "BarChart",
      title: "Analytics Dashboard",
      description: "View detailed analytics and platform insights",
      color: "bg-indigo-500",
      action: () => console.log("Navigate to analytics")
    }
  ];

  const handleDismissAlert = (index) => {
    setAlerts(alerts?.filter((_, i) => i !== index));
  };

  const handleToolClick = (item) => {
    console.log("Tool clicked:", item?.action);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="main-content">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          {/* Page Header */}
          <div className="mb-6 md:mb-8">
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-2">
              Admin Dashboard
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              Comprehensive tournament management and user administration
            </p>
          </div>

          {/* System Alerts */}
          {alerts?.length > 0 &&
          <div className="mb-6 md:mb-8">
              {alerts?.map((alert, index) =>
            <SystemAlert
              key={index}
              type={alert?.type}
              title={alert?.title}
              message={alert?.message}
              actionLabel={alert?.actionLabel}
              onAction={alert?.onAction}
              onDismiss={() => handleDismissAlert(index)} />

            )}
            </div>
          }

          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
            {metricsData?.map((metric, index) =>
            <MetricCard
              key={index}
              title={metric?.title}
              value={metric?.value}
              icon={metric?.icon}
              trend={metric?.trend}
              trendValue={metric?.trendValue}
              color={metric?.color} />

            )}
          </div>

          {/* Quick Actions */}
          <div className="mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-6">
              Quick Actions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {quickActions?.map((action, index) =>
              <QuickActionCard
                key={index}
                title={action?.title}
                description={action?.description}
                icon={action?.icon}
                actionLabel={action?.actionLabel}
                actionPath={action?.actionPath}
                color={action?.color} />

              )}
            </div>
          </div>

          {/* Admin Tools */}
          <div className="mb-6 md:mb-8">
            <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground mb-4 md:mb-6">
              Admin Tools
            </h2>
            <div className="bg-card rounded-lg shadow-elevation-sm border border-border p-4 md:p-6">
              <div className="flex flex-col sm:flex-row sm:items-start gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30">
                      <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-orange-600 dark:text-orange-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                    </span>
                    <h3 className="text-base md:text-lg font-semibold text-foreground">Check Deadline Reminders</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Manually trigger the deadline reminder check to send notifications for upcoming prediction deadlines. Use for testing or emergency notifications.
                  </p>
                  {reminderResult && (
                    <div className={`text-sm px-3 py-2 rounded-md ${reminderResult?.success ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'}`}>
                      {reminderResult?.success ? '✓ ' : '✗ '}{reminderResult?.message}
                    </div>
                  )}
                </div>
                <div className="sm:flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={handleCheckDeadlineReminders}
                    disabled={reminderLoading}
                    iconName={reminderLoading ? undefined : "Bell"}
                    iconPosition="left"
                    className="w-full sm:w-auto border-orange-300 text-orange-700 hover:bg-orange-50 dark:border-orange-700 dark:text-orange-400 dark:hover:bg-orange-900/20"
                  >
                    {reminderLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="animate-spin inline-block w-4 h-4 border-2 border-orange-400 border-t-transparent rounded-full"></span>
                        Checking...
                      </span>
                    ) : 'Run Reminder Check'}
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="mb-6 md:mb-8">
            <div className="bg-card rounded-lg shadow-elevation-sm border border-border">
              <div className="p-4 md:p-6 border-b border-border">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-semibold text-foreground">
                    Recent Activity
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="RefreshCw"
                    iconPosition="left"
                    className="text-xs md:text-sm"
                    onClick={fetchRecentActivities}>

                    Refresh
                  </Button>
                </div>
              </div>
              <div className="divide-y divide-border max-h-[600px] overflow-y-auto">
                {recentActivities?.length > 0 ? (
                  recentActivities?.map((activity, index) =>
                  <ActivityItem
                    key={index}
                    type={activity?.type}
                    user={activity?.user}
                    action={activity?.action}
                    timestamp={activity?.timestamp}
                    details={activity?.details} />

                  )
                ) : (
                  <div className="p-8 text-center text-muted-foreground">
                    <p>No recent activity to display</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Recent Tournaments */}
          <div className="mt-6 md:mt-8">
            <div className="flex items-center justify-between mb-4 md:mb-6">
              <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold text-foreground">
                Recent Tournaments
              </h2>
              <Button
                variant="outline"
                onClick={() => navigate('/tournaments-list')}
                iconName="ArrowRight"
                iconPosition="right"
                className="text-sm md:text-base">

                View All
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {recentTournaments?.map((tournament) =>
              <RecentTournamentCard
                key={tournament?.id}
                tournament={tournament} />

              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );

};

export default AdminDashboard;